import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BellRing, Loader2, RefreshCw, CheckCheck, ArrowRight, ShieldAlert, Boxes,
} from 'lucide-react';
import { toast } from 'sonner';
import { SEV_STYLE, timeAgo } from '@/lib/sentinelConstants';
import ProcessHelp from '@/components/ProcessHelp';

export default function DriftAlertCenterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getDriftAlerts();
      setAlerts(r.alerts || []);
      setUnread(r.unread || 0);
    } catch (e) {
      toast.error(e.message || 'Failed to load drift alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (a) => {
    setBusy(a.notification_id);
    try {
      await api.markDriftAlertsRead(a.notification_id);
      setAlerts((prev) => prev.map((x) => (x.notification_id === a.notification_id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - (a.read ? 0 : 1)));
    } catch (e) {
      toast.error(e.message || 'Could not mark read');
    } finally {
      setBusy('');
    }
  };

  const markAllRead = async () => {
    setBusy('all');
    try {
      await api.markDriftAlertsRead();
      setAlerts((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      toast.success('All drift alerts marked read');
    } catch (e) {
      toast.error(e.message || 'Could not mark all read');
    } finally {
      setBusy('');
    }
  };

  const openScorecard = (a) => {
    if (!a.read) markRead(a);
    navigate(a.model_id ? `/admin/predictive-models?model=${encodeURIComponent(a.model_id)}` : '/admin/predictive-models');
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="drift-alert-center-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BellRing className="w-6 h-6 text-rose-500" /> Drift Alert Center
            {unread ? <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/40" data-testid="drift-unread-badge">{unread} new</Badge> : null}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every time one of your predictive models tips into <span className="font-medium text-rose-500">critical drift</span>,
            it lands here — with the model, when it happened, and a one-tap jump to its scorecard to recertify.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ProcessHelp
            testId="drift-help"
            title="Drift Alert Center"
            section="drift-alerts"
            intro="A dedicated inbox for model owners and stewards — so a model tipping into critical drift never slips by unnoticed."
            steps={[
              'Unread alerts are highlighted with a count at the top.',
              'Each alert shows the model, when it happened and the drift severity.',
              'Click Scorecard to jump to that model\u2019s registry entry and recertify it.',
              'Mark read per alert, or Mark all read to clear the queue.',
              'Alerts fire the moment a model turns critical; you are not re-alerted while it stays critical.',
            ]}
          />
          <Button variant="outline" size="sm" onClick={load} data-testid="drift-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={markAllRead} disabled={!unread || busy === 'all'} data-testid="drift-mark-all-read">
            {busy === 'all' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-1.5" />}
            Mark all read
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 py-16 text-center" data-testid="drift-alerts-empty">
          <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground mb-1">No drift alerts — all monitored models are holding steady.</p>
          <p className="text-xs text-muted-foreground/70">You'll be notified here the moment a model tips into critical drift.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div key={a.notification_id}
              data-testid={`drift-alert-${a.notification_id}`}
              className={`rounded-xl border p-4 flex items-start gap-3 ${a.read ? 'border-border/50 bg-card/40' : 'border-rose-500/40 bg-rose-500/[0.05]'}`}>
              {!a.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" data-testid={`drift-alert-dot-${a.notification_id}`} />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${SEV_STYLE.critical} text-[11px]`}>Critical drift</Badge>
                  <span className="font-medium flex items-center gap-1 text-sm">
                    <Boxes className="w-3.5 h-3.5 text-indigo-400" /> {a.model_name || a.model_id || 'Model'}
                  </span>
                  <span className="text-xs text-muted-foreground">· {timeAgo(a.created_at)}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5">{a.message}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => openScorecard(a)} data-testid={`drift-view-scorecard-${a.notification_id}`}>
                  Scorecard <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
                {!a.read && (
                  <Button size="sm" variant="ghost" className="h-6 text-[11px] text-muted-foreground"
                    onClick={() => markRead(a)} disabled={busy === a.notification_id} data-testid={`drift-mark-read-${a.notification_id}`}>
                    {busy === a.notification_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Mark read'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
